import React from 'react';

import type { AuroraColors, BlobStyleProps } from './types';

const DEFAULT_AURORA_COLORS: AuroraColors = {
  layer1: '#FF508C',
  layer2: '#78B4FF',
  layer3: '#64FFC8',
  layer4: '#FFC850',
};

/** Parse `#RRGGBB` into the `r,g,b` string format used inside rgba() literals. */
function hexToRgbString(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '255,255,255';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff}`;
}

/**
 * Aurora blob — layered colored gradient blobs on a dark base, masked to a
 * circle. Uses `mix-blend-mode: plus-lighter` for additive color without
 * washing to white. Ported from the wiki mock.
 *
 * `isolation: isolate` keeps blend modes inside the orb so a light page
 * background cannot blow the layers out to white.
 */
export const AuroraBlob: React.FC<BlobStyleProps & { colors?: AuroraColors }> = ({ level, size, colors }) => {
  const scale = 1 + level * 0.18;
  const c = colors ?? DEFAULT_AURORA_COLORS;
  const layers = [
    { color: hexToRgbString(c.layer1), delay: '0s', duration: '7s', anim: 0 },
    { color: hexToRgbString(c.layer2), delay: '-2s', duration: '8s', anim: 1 },
    { color: hexToRgbString(c.layer3), delay: '-4s', duration: '9s', anim: 2 },
    { color: hexToRgbString(c.layer4), delay: '-1s', duration: '6s', anim: 3 },
  ] as const;

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        borderRadius: '50%',
        overflow: 'hidden',
        isolation: 'isolate',
        transform: `scale(${scale})`,
        transition: 'transform 100ms ease-out',
        // Deep violet core; saturated plum rim instead of near-black.
        background:
          'radial-gradient(circle at 50% 50%, #2a1048 0%, #1a0830 48%, #3d1868 78%, #4a2088 100%)',
      }}
      aria-hidden
    >
      {layers.map((l, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            inset: '-10%',
            background: `radial-gradient(circle at 50% 50%, rgba(${l.color},${
              0.82 + level * 0.14
            }) 0%, rgba(${l.color},0.58) 28%, rgba(${l.color},0.28) 50%, rgba(${l.color},0) 62%)`,
            mixBlendMode: 'plus-lighter',
            filter: `blur(${8 + (1 - level) * 6}px)`,
            animation: `aurora-${l.anim} ${l.duration} ease-in-out ${l.delay} infinite`,
          }}
        />
      ))}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 35% 30%, rgba(255,255,255,0.2), transparent 22%)',
          mixBlendMode: 'screen',
          pointerEvents: 'none',
        }}
      />
      <style>{`
        @keyframes aurora-0 {
          0%,100% { transform: translate(-22%, -18%) scale(1); }
          50%     { transform: translate(18%, 14%)  scale(1.2); }
        }
        @keyframes aurora-1 {
          0%,100% { transform: translate(20%, -16%) scale(1.15); }
          50%     { transform: translate(-18%, 18%) scale(0.95); }
        }
        @keyframes aurora-2 {
          0%,100% { transform: translate(-14%, 22%) scale(0.95); }
          50%     { transform: translate(16%, -20%) scale(1.15); }
        }
        @keyframes aurora-3 {
          0%,100% { transform: translate(16%, 18%) scale(1.05); }
          50%     { transform: translate(-20%, -14%) scale(1.25); }
        }
      `}</style>
    </div>
  );
};

export default AuroraBlob;
